/**
 * Session state.
 *
 * The token store is async because storage is; screens need the answer
 * synchronously to decide whether to fetch. This mirrors the token store into
 * observable state, hydrated once at startup.
 *
 * Without it, every authenticated query fires for signed-out visitors and the
 * app makes a run of 401s on the home screen — which is what a browser console
 * showed the first time the web build ran.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

import { config } from '../../app/config';
import { tokenStore } from './tokenStore';

interface AuthState {
  /** Null until storage has been read; screens should not decide before then. */
  isSignedIn: boolean | null;
  hydrate: () => Promise<void>;
  signedIn: () => void;
  signedOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isSignedIn: null,

  hydrate: async () => {
    // The demo build has no sign-in screen, so it presents a signed-in donor.
    // Without this, My Donations, Impact, Notifications and Profile would all
    // sit behind a wall nobody can get past.
    if (config.demoMode) {
      set({ isSignedIn: true });
      return;
    }
    set({ isSignedIn: await tokenStore.isSignedIn() });
  },

  signedIn: () => set({ isSignedIn: true }),

  signedOut: async () => {
    await tokenStore.clear();
    set({ isSignedIn: false });
  },
}));

/** True only once storage has been read and a session was found. */
export const useIsSignedIn = (): boolean => useAuthStore((s) => s.isSignedIn === true);

/** True while the session is still being read from storage. */
export const useAuthLoading = (): boolean => useAuthStore((s) => s.isSignedIn === null);

/** Hydrates the session once, at the app root. */
export function useHydrateAuth(): void {
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    void hydrate();
  }, [hydrate]);
}
